import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import TaskDetailsPage from "./tasks/TaskDetailsPage";
import TaskForm from "./TaskForm";



function TaskFormRoute({
  currentUser = null,
  users = [],
  teams = [],
  subTeams = [],
  darkMode = false,
  onUpdateTaskInState = null,
  onDeleteTask = null,
}) {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const fileInputRef = useRef(null);

  const editParam = searchParams.get("edit");
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(editParam === "true");
  const [uploading, setUploading] = useState(false);

  const getTaskAssignees = useCallback((t) => {
    if (!Array.isArray(t?.task_assignees)) return [];

    return t.task_assignees
      .map((a) => {
        const profile = Array.isArray(a.profiles) ? a.profiles[0] : a.profiles;
        const matchedUser = users.find((u) => u.id === a.user_id);
        const finalUser = profile || matchedUser;

        if (!finalUser) return null;

        return {
          id: finalUser.id || a.user_id,
          name: finalUser.name || "",
          email: finalUser.email || "",
        };
      })
      .filter(Boolean);
  }, [users]);

  const getAssigneeText = useCallback((t) => {
    const list = getTaskAssignees(t);
    const names = list.map((u) => u.name || u.email).filter(Boolean);
    return names.length ? names.join(", ") : "Unassigned";
  }, [getTaskAssignees]);

  const getFirstAssigneeId = useCallback((t) => {
    const list = getTaskAssignees(t);
    return list.length ? list[0].id : null;
  }, [getTaskAssignees]);

  const loadTask = useCallback(async (shouldSyncParent = false) => {
    if (!taskId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data: taskData, error: taskError } = await supabase
      .from("tasks")
      .select("*, teams:team_id(id, name), sub_teams:sub_team_id(id, name)")
      .eq("id", taskId)
      .single();

    if (taskError) {
      console.error("Failed to load task:", taskError);
      setTask(null);
      setLoading(false);
      return;
    }

    console.log("Loaded task team debug:", {
      team_id: taskData.team_id,
      sub_team_id: taskData.sub_team_id,
      teams: taskData.teams,
      sub_teams: taskData.sub_teams
    });

    const [
      assigneesRes,
      userCasesRes,
      updatesRes,
      notesRes,
      issuesRes,
      extensionRes,
    ] = await Promise.all([
      supabase
        .from("task_assignees")
        .select(`
          id,
          task_id,
          user_id,
          assigned_by,
          assigned_at,
          profiles:user_id (
            id,
            name,
            email,
            role,
            photo_url
          )
        `)
        .eq("task_id", taskId),
      supabase.from("task_user_cases").select("*").eq("task_id", taskId).order("created_at", { ascending: false }),
      supabase.from("task_updates").select("*").eq("task_id", taskId).order("created_at", { ascending: false }),
      supabase.from("task_notes").select("*").eq("task_id", taskId).order("created_at", { ascending: false }),
      supabase
        .from("task_issues")
        .select("*, reporter:profiles!task_issues_reported_by_fkey(id, name, email)")
        .eq("task_id", taskId)
        .order("created_at", { ascending: false }),
      supabase.from("task_extension_requests").select("*").eq("task_id", taskId).order("created_at", { ascending: false }),
    ]);

    const updatedTask = {
      ...taskData,
      task_assignees: assigneesRes.data || [],
      task_user_cases: userCasesRes.data || [],
      task_updates: updatesRes.data || [],
      task_notes: notesRes.data || [],
      task_issues: issuesRes.data || [],
      task_extension_requests: extensionRes.data || [],
    };

    setTask(updatedTask);
    if (shouldSyncParent && onUpdateTaskInState) {
      onUpdateTaskInState(updatedTask);
    }

    setLoading(false);
  }, [taskId, onUpdateTaskInState]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  useEffect(() => {
    setEditing(editParam === "true");
  }, [editParam]);

  const cleanTaskPayload = (payload) => {
    const cleaned = { ...payload };

    if (cleaned.startDate) {
      cleaned.start_date = cleaned.startDate;
      delete cleaned.startDate;
    }
    if (cleaned.dueDate) {
      cleaned.due_date = cleaned.dueDate;
      delete cleaned.dueDate;
    }
    if (cleaned.loopDurationValue) {
      cleaned.loop_duration_value = cleaned.loopDurationValue;
      delete cleaned.loopDurationValue;
    }
    if (cleaned.loopDurationUnit) {
      cleaned.loop_duration_unit = cleaned.loopDurationUnit;
      delete cleaned.loopDurationUnit;
    }
    if (cleaned.createNextAfterCompletion) {
      cleaned.create_next_after_completion = cleaned.createNextAfterCompletion;
      delete cleaned.createNextAfterCompletion;
    }

    const ALLOWED_TASK_COLUMNS = new Set([
      'id',
      'title',
      'description',
      'assignee_id',
      'start_date',
      'due_date',
      'priority',
      'status',
      'budget',
      'extended_due_dates',
      'team_id',
      'sub_team_id',
      'created_by',
      'updated_at',
      'deleted_at',
      'attachment_url',
      'attachments',
      'assigned_by',
      'progress_status',
      'progress_report',
      'timer_start',
      'hours',
      'extended_due_date_1',
      'extended_due_date_2',
      'assignee',
      'assignee_email',
      'created_at',
      'task_note',
      'task_type',
      'repeat_frequency',
      'repeat_every',
      'repeat_start_date',
      'repeat_end_date',
      'repeat_occurrences',
      'repeat_days',
      'loop_duration_value',
      'loop_duration_unit',
      'create_next_after_completion'
    ]);

    for (const key of Object.keys(cleaned)) {
      if (!ALLOWED_TASK_COLUMNS.has(key)) {
        delete cleaned[key];
      }
    }

    return cleaned;
  };

  const handleEditSave = async (payload) => {
    if (!task?.id) return;

    const cleanedPayload = cleanTaskPayload(payload);

    const { error } = await supabase
      .from("tasks")
      .update({
        ...cleanedPayload,
        updated_at: new Date().toISOString(),
      })
      .eq("id", task.id);

    if (error) {
      console.error("Failed to update task:", error);
      alert("Failed to update task: " + error.message);
      return;
    }

    const cleanAssignedUsers = (payload.assignees || payload.assigned_users || []).filter(
      (u) => u.id || u.name || u.email
    );

    await supabase
      .from("task_assignees")
      .delete()
      .eq("task_id", task.id);

    if (cleanAssignedUsers.length > 0) {
      const toInsert = cleanAssignedUsers
        .map((u) => {
          const matched = u.id ? u : users.find((user) => user.name === u.name || user.email === u.email || user.id === u.id);
          return matched?.id ? {
            task_id: task.id,
            user_id: matched.id,
            assigned_by: currentUser?.id || null,
          } : null;
        })
        .filter(Boolean);

      const uniqueToInsert = [];
      const seen = new Set();
      for (const item of toInsert) {
        if (!seen.has(item.user_id)) {
          seen.add(item.user_id);
          uniqueToInsert.push(item);
        }
      }

      if (uniqueToInsert.length > 0) {
        const { error: assigneeInsertError } = await supabase
          .from("task_assignees")
          .insert(uniqueToInsert);

        if (assigneeInsertError) {
          console.error("Failed to save assignees:", assigneeInsertError);
        }
      }
    }

    setEditing(false);
    await loadTask(true);
  };


  const handleAddIssue = async ({ title, description, priority = "Medium", files = [] }) => {
    console.log("handleAddIssue called:", { title, description, priority, files });
    if (!task?.id) return;

    if (!title?.trim()) {
      alert("Please enter issue title.");
      return;
    }

    const uploadedAttachments = [];

    for (const file of files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `task-issues/${task.id}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("attachments")
        .upload(filePath, file);

      if (uploadError) {
        alert("Issue file upload failed: " + uploadError.message);
        return;
      }

      const { data } = supabase.storage.from("attachments").getPublicUrl(filePath);

      uploadedAttachments.push({
        name: file.name,
        url: data.publicUrl,
        size: `${Math.round(file.size / 1024)} KB`,
        type: file.type || "file",
        uploaded_at: new Date().toISOString(),
      });
    }

    const { error } = await supabase.from("task_issues").insert({
      task_id: task.id,
      title: title.trim(),
      description: description?.trim() || null,
      priority: priority || "Medium",
      status: "Open",
      reported_by: currentUser?.id || null,
      assigned_to: getFirstAssigneeId(task),
      attachments: uploadedAttachments,
    });

    if (error) {
      console.error("Issue insert failed:", error);
      alert("Failed to add issue: " + error.message);
      return false;
    }

    console.log("Issue added successfully");

    await loadTask();
    return true;
  };

  const handleAdvanceIssue = async (issue) => {
    if (!issue?.id) return false;

    const nextStatus =
      issue.status === "Open"
        ? "In Progress"
        : issue.status === "In Progress"
        ? "Resolved"
        : "Resolved";

    const { error } = await supabase
      .from("task_issues")
      .update({
        status: nextStatus,
      })
      .eq("id", issue.id);

    if (error) {
      console.error("Issue status update failed:", error);
      alert("Failed to update issue: " + error.message);
      return false;
    }

    await loadTask();
    return true;
  };

  const handleAddUserCase = async ({
    title,
    description,
    acceptanceCriteria,
    testCases = [],
    files = [],
  }) => {
    if (!task?.id) return false;

    if (!title?.trim()) {
      alert("Please enter user story title.");
      return false;
    }

    const parsedTestCases = (Array.isArray(testCases) ? testCases : [testCases])
      .map((tc) => String(tc || "").trim())
      .filter(Boolean)
      .map((tc) => ({ title: tc, status: "Pending" }));

    const uploadedAttachments = [];

    for (const file of files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `user-cases/${task.id}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("attachments")
        .upload(filePath, file);

      if (uploadError) {
        alert("User story file upload failed: " + uploadError.message);
        return false;
      }

      const { data } = supabase.storage
        .from("attachments")
        .getPublicUrl(filePath);

      uploadedAttachments.push({
        name: file.name,
        url: data.publicUrl,
        size: `${Math.round(file.size / 1024)} KB`,
        type: file.type || "file",
        uploaded_at: new Date().toISOString(),
      });
    }

    const { error } = await supabase.from("task_user_cases").insert({
      task_id: task.id,
      title: title.trim(),
      description: description?.trim() || null,
      acceptance_criteria: acceptanceCriteria?.trim() || null,
      status: "Pending",
      test_cases: parsedTestCases,
      test_cases_count: parsedTestCases.length,
      attachments: uploadedAttachments,
      created_by: currentUser?.id || null,
    });

    if (error) {
      console.error("User story insert failed:", error);
      alert("Failed to add user story: " + error.message);
      return false;
    }

    await loadTask();
    return true;
  };

  const handleAddAssignee = async (user) => {
    if (!task?.id || !user?.id) return false;

    const finalUser =
      users.find((u) => u.id === user.id) ||
      users.find(
        (u) => u.email?.toLowerCase() === user.email?.toLowerCase()
      ) ||
      user;

    const { error } = await supabase.from("task_assignees").upsert(
      {
        task_id: task.id,
        user_id: finalUser.id,
        assigned_by: currentUser?.id || null,
      },
      {
        onConflict: "task_id,user_id",
      }
    );

    if (error) {
      alert("Failed to add assignee: " + error.message);
      return false;
    }

    const { error: notificationError } = await supabase
      .from("notifications")
      .insert({
        user_id: finalUser.id,
        task_id: task.id,
        title: "Task assigned",
        message: task.task_note || `You have been assigned "${task.title}"`,
        type: "TASK_CREATED",
        action_role: "assignee",
        assignee_name: finalUser.name || finalUser.email,
        assigned_to: finalUser.name || finalUser.email,
        attachments: task.attachments || [],
        is_read: false,
        is_dismissed: false,
      });

    if (notificationError) {
      console.error("Notification failed:", notificationError);
      alert("Assignee added, but notification failed: " + notificationError.message);
    }

    if (finalUser.email) {
      try {
        const response = await fetch("/api/email/task-created", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-internal-token": process.env.REACT_APP_INTERNAL_TOKEN || "" },
          body: JSON.stringify({
            users: [
              {
                id: finalUser.id,
                name: finalUser.name || finalUser.email,
                email: finalUser.email,
              },
            ],
            task: {
              id: task.id,
              title: task.title,
              description: task.task_note || task.description,
              assigned_to: finalUser.name || finalUser.email,
              assigned_by: currentUser?.name || currentUser?.email || "N/A",
              start_date: task.start_date || task.repeat_start_date,
              due_date: task.due_date,
              priority: task.priority || "Medium",
              task_type: task.task_type,
              repeat_frequency: task.repeat_frequency,
              repeat_every: task.repeat_every,
              attachments: task.attachments || [],
            },
            taskUrl: `${window.location.origin}/tasks/${task.id}`,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          console.error("Email failed:", result);
          alert("Assignee added, but email failed: " + (result.error || "Unknown error"));
        }
      } catch (emailError) {
        console.error("Email failed:", emailError);
        alert("Assignee added, but email failed.");
      }
    }

    await loadTask();
    return true;
  };

  const handleAddExtension = async ({
    requestedDueDate,
    reason,
    requestNote = "",
    files = [],
  }) => {
    if (!task?.id) return false;

    if (!requestedDueDate?.trim()) {
      alert("Please select requested due date.");
      return false;
    }

    if (!reason?.trim()) {
      alert("Please enter reason.");
      return false;
    }

    const uploadedAttachments = [];

    for (const file of files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `extension-requests/${task.id}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("attachments")
        .upload(filePath, file);

      if (uploadError) {
        alert("Extension file upload failed: " + uploadError.message);
        return false;
      }

      const { data } = supabase.storage.from("attachments").getPublicUrl(filePath);

      uploadedAttachments.push({
        name: file.name,
        url: data.publicUrl,
        size: `${Math.round(file.size / 1024)} KB`,
        type: file.type || "file",
        uploaded_at: new Date().toISOString(),
      });
    }

    const { error } = await supabase.from("task_extension_requests").insert({
      task_id: task.id,
      requested_due_date: requestedDueDate.trim(),
      reason: reason.trim(),
      request_note: requestNote.trim() || null,
      attachments: uploadedAttachments,
      impact: "Medium",
      status: "Pending",
      requested_by: currentUser?.id || null,
    });

    if (error) {
      alert("Failed to request extension: " + error.message);
      return false;
    }

    const { data: departmentAdmins, error: adminError } = await supabase
      .from("profiles")
      .select("id, name, email")
      .eq("team_id", task.team_id)
      .eq("role", "Department Admin")
      .eq("is_active", true)
      .is("deleted_at", null);

    if (adminError) {
      console.error("Department admin fetch failed:", adminError);
    }

    const admins = departmentAdmins || [];

    if (admins.length > 0) {
      await supabase.from("notifications").insert(
        admins.map((admin) => ({
          user_id: admin.id,
          task_id: task.id,
          title: task.title || "Task Extension Request",
          message: requestNote || reason || "Extension request submitted.",
          request_note: requestNote || reason || null,
          type: "TASK_EXTENSION_REQUEST",
          action_role: "department_admin",
          assignee_name: currentUser?.name || currentUser?.email || "Unknown User",
          assigned_to: currentUser?.name || currentUser?.email || "Unknown User",
          is_read: false,
          is_dismissed: false,
        }))
      );

      const emailAdmins = admins.filter((admin) => admin.email);

      console.log("Sending extension email to:", emailAdmins);

      if (emailAdmins.length > 0) {
        try {
          const response = await fetch("/api/email/extension-request", {
            method: "POST",
            headers: { "Content-Type": "application/json", "x-internal-token": process.env.REACT_APP_INTERNAL_TOKEN || "" },
            body: JSON.stringify({
              users: emailAdmins,
              task: {
                id: task.id,
                title: task.title,
                description: task.description,
                task_note: task.task_note,
                assignee: getAssigneeText(task) || currentUser?.name || "User",
                assigned_to: getAssigneeText(task) || currentUser?.name || "User",
                assigned_by: currentUser?.name || currentUser?.email || "N/A",
                priority: task.priority || "Medium",
                start_date: task.start_date,
                due_date: task.due_date,
                requested_due_date: requestedDueDate.trim(),
                reason: reason.trim(),
                request_note: requestNote.trim(),
                attachments: uploadedAttachments,
              },
              taskUrl: `${window.location.origin}/tasks/${task.id}?tab=extension`,
            }),
          });

          const result = await response.json();
          console.log("Extension email response:", result);

          if (!response.ok) {
            alert("Extension email failed: " + (result.error || "Unknown error"));
          }
        } catch (mailError) {
          console.error("Extension request email failed:", mailError);
        }
      }
    }

    await loadTask();
    return true;
  };

  const handleApproveExtension = async (requestId) => {
    const request = task?.task_extension_requests?.find((r) => r.id === requestId);

    if (!request?.requested_due_date) {
      alert("Requested due date not found.");
      return;
    }

    await supabase
      .from("task_extension_requests")
      .update({
        status: "Approved",
        reviewed_by: currentUser?.id || null,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", requestId);

    await supabase
      .from("tasks")
      .update({
        due_date: request.requested_due_date,
        updated_at: new Date().toISOString(),
      })
      .eq("id", task.id);

    await loadTask();
  };

  const handleRejectExtension = async (requestId) => {
    await supabase
      .from("task_extension_requests")
      .update({
        status: "Rejected",
        reviewed_by: currentUser?.id || null,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", requestId);

    await loadTask();
  };

  const handleUploadFile = async ({ taskId, fileName, file }) => {
    if (!taskId || !file) return null;
    setUploading(true);

    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `tasks/${taskId}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("attachments")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from("attachments")
        .getPublicUrl(filePath);

      const savedSupabaseFile = {
        name: fileName || file.name,
        url: publicUrlData.publicUrl,
        size: file.size,
        type: file.type || "file",
        uploaded_by: currentUser?.name || currentUser?.email || "User",
        uploaded_at: new Date().toISOString(),
        source: "Task",
      };

      const existingAttachments = Array.isArray(task.attachments)
        ? [...task.attachments]
        : [];

      const nextAttachments = [...existingAttachments, savedSupabaseFile];

      const { error: updateError } = await supabase
        .from("tasks")
        .update({
          attachments: nextAttachments,
          updated_at: new Date().toISOString(),
        })
        .eq("id", taskId);

      if (updateError) throw updateError;

      await loadTask();

      return savedSupabaseFile;
    } catch (error) {
      console.error("File upload failed:", error);
      alert("File upload failed: " + error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!task?.id || files.length === 0) return;

    setUploading(true);

    try {
      const existingAttachments = Array.isArray(task.attachments)
        ? [...task.attachments]
        : [];

      const uploadedAttachments = [];

      for (const file of files) {
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
        const filePath = `tasks/${task.id}/${Date.now()}_${safeName}`;

        const { error: uploadError } = await supabase.storage
          .from("attachments")
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: publicUrlData } = supabase.storage
          .from("attachments")
          .getPublicUrl(filePath);

        uploadedAttachments.push({
          name: file.name,
          url: publicUrlData.publicUrl,
          size: `${Math.round(file.size / 1024)} KB`,
          type: file.type || "file",
          uploaded_by: currentUser?.name || currentUser?.email || "User",
          uploaded_at: new Date().toISOString(),
        });
      }

      const nextAttachments = [...existingAttachments, ...uploadedAttachments];

      const { error: updateError } = await supabase
        .from("tasks")
        .update({
          attachments: nextAttachments,
          updated_at: new Date().toISOString(),
        })
        .eq("id", task.id);

      if (updateError) throw updateError;

      await loadTask();
    } catch (error) {
      console.error("File upload failed:", error);
      alert("File upload failed: " + error.message);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const handleDeleteFiles = async (filesToDelete) => {
    if (!task?.id || !Array.isArray(filesToDelete) || filesToDelete.length === 0) return false;

    try {
      const urlsToDelete = filesToDelete.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);

      // 1. Delete from Supabase Storage
      const pathsToDelete = urlsToDelete.map((url) => {
        const parts = url.split("/attachments/");
        return parts.length > 1 ? parts[1] : null;
      }).filter(Boolean);

      if (pathsToDelete.length > 0) {
        const { error: storageError } = await supabase.storage
          .from("attachments")
          .remove(pathsToDelete);

        if (storageError) {
          console.error("Storage delete failed:", storageError);
        }
      }

      // 2. Separate files by their source so we update the correct database tables
      const taskFiles = [];
      const issueFiles = [];
      const extensionFiles = [];
      const userCaseFiles = [];

      filesToDelete.forEach((file) => {
        const source = file.source;
        if (source === "Issue") {
          issueFiles.push(file);
        } else if (source === "Request Extension") {
          extensionFiles.push(file);
        } else if (source === "User Story") {
          userCaseFiles.push(file);
        } else {
          taskFiles.push(file);
        }
      });

      const promises = [];

      // A. Delete from tasks.attachments
      if (taskFiles.length > 0) {
        const taskUrls = taskFiles.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);
        const existingAttachments = Array.isArray(task.attachments) ? task.attachments : [];
        const nextAttachments = existingAttachments.filter((att) => {
          const attUrl = att.url || att.publicUrl || att.public_url || att.file_url || att.download_url;
          return !taskUrls.includes(attUrl);
        });

        promises.push(
          supabase
            .from("tasks")
            .update({
              attachments: nextAttachments,
              updated_at: new Date().toISOString(),
            })
            .eq("id", task.id)
        );
      }

      // B. Delete from task_issues.attachments
      if (issueFiles.length > 0 && Array.isArray(task.task_issues)) {
        task.task_issues.forEach((issue) => {
          const issueUrls = issueFiles.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);
          const existing = Array.isArray(issue.attachments) ? issue.attachments : [];
          const next = existing.filter((att) => {
            const attUrl = att.url || att.publicUrl || att.public_url || att.file_url || att.download_url;
            return !issueUrls.includes(attUrl);
          });

          if (existing.length !== next.length) {
            promises.push(
              supabase
                .from("task_issues")
                .update({ attachments: next })
                .eq("id", issue.id)
            );
          }
        });
      }

      // C. Delete from task_extension_requests.attachments
      if (extensionFiles.length > 0 && Array.isArray(task.task_extension_requests)) {
        task.task_extension_requests.forEach((req) => {
          const extUrls = extensionFiles.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);
          const existing = Array.isArray(req.attachments) ? req.attachments : [];
          const next = existing.filter((att) => {
            const attUrl = att.url || att.publicUrl || att.public_url || att.file_url || att.download_url;
            return !extUrls.includes(attUrl);
          });

          if (existing.length !== next.length) {
            promises.push(
              supabase
                .from("task_extension_requests")
                .update({ attachments: next })
                .eq("id", req.id)
            );
          }
        });
      }

      // D. Delete from task_user_cases.attachments
      if (userCaseFiles.length > 0 && Array.isArray(task.task_user_cases)) {
        task.task_user_cases.forEach((uc) => {
          const ucUrls = userCaseFiles.map((f) => f.url || f.publicUrl || f.public_url || f.file_url || f.download_url).filter(Boolean);
          const existing = Array.isArray(uc.attachments) ? uc.attachments : [];
          const next = existing.filter((att) => {
            const attUrl = att.url || att.publicUrl || att.public_url || att.file_url || att.download_url;
            return !ucUrls.includes(attUrl);
          });

          if (existing.length !== next.length) {
            promises.push(
              supabase
                .from("task_user_cases")
                .update({ attachments: next })
                .eq("id", uc.id)
            );
          }
        });
      }

      const results = await Promise.all(promises);
      for (const res of results) {
        if (res.error) throw res.error;
      }

      await loadTask();
      return true;
    } catch (error) {
      console.error("Failed to delete files:", error);
      alert("Failed to delete files: " + error.message);
      return false;
    }
  };

  const handleDeleteAssignees = async ({ taskId, userIds }) => {
    const { error } = await supabase
      .from("task_assignees")
      .delete()
      .eq("task_id", taskId)
      .in("user_id", userIds);

    if (error) {
      console.error(error);
      return false;
    }

    await loadTask();
    return true;
  };



  if (loading) {
    return (
      <div
        style={{
          padding: 40,
          fontSize: 18,
          fontWeight: 600,
        }}
      >
        Loading task...
      </div>
    );
  }

  if (!task) {
    return (
      <div
        style={{
          padding: 40,
          fontSize: 18,
          fontWeight: 600,
          color: "red",
        }}
      >
        Task not found.
      </div>
    );
  }

  if (editing) {
    return (
      <TaskForm
        initial={task}
        currentUser={currentUser}
        users={users}
        teams={teams}
        subTeams={subTeams}
        darkMode={darkMode}
        readOnly={false}
        onClose={() => {
          setEditing(false);
          if (searchParams.get("edit")) {
            navigate(`/tasks/${taskId}`, { replace: true });
          }
        }}
        onSave={handleEditSave}
      />
    );
  }

  return (
    <>
      <input id="fileInput-1" name="fileInput-1"
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      {uploading && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            padding: "12px 18px",
            background: "#76AB3F",
            color: "white",
            borderRadius: "10px",
            fontWeight: 800,
          }}
        >
          Uploading file...
        </div>
      )}

      <TaskDetailsPage
        task={task}
        currentUser={currentUser}
        users={users}
        teams={teams}
        subTeams={subTeams}
        initialTab={initialTab}
        darkMode={darkMode}
        onBack={() => navigate("/tasks")}
        onEdit={() => {
          setEditing(true);
          navigate(`/tasks/${taskId}?edit=true`, { replace: true });
        }}
        onUploadFile={handleUploadFile}
        onDeleteFiles={handleDeleteFiles}
        onAddExtension={handleAddExtension}
        onApproveExtension={handleApproveExtension}
        onRejectExtension={handleRejectExtension}
        onAddIssue={handleAddIssue}
        onAddUserCase={handleAddUserCase}
        onAddAssignee={handleAddAssignee}
        onDeleteAssignees={handleDeleteAssignees}
        onDeleteTask={onDeleteTask}
        onAdvanceIssue={handleAdvanceIssue}
      />
    </>
  );
}

export default TaskFormRoute;